import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, RefreshCw, Wifi, WifiOff, Edit2, ExternalLink, Router, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { toast } from 'sonner';

const IPConflictReport = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ conflicts: [], total_conflict_ips: 0, total_conflict_customers: 0 });
  const [routerFilter, setRouterFilter] = useState('all');
  const navigate = useNavigate();
  const { selectedBranch } = useApp();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchConflicts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = (selectedBranch && selectedBranch !== 'all') ? { branch_id: selectedBranch } : {};
      const res = await axios.get(`${API_URL}/api/v2/customers/ip-conflicts`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      if (res.data?.success) {
        setData(res.data);
      } else {
        toast.error('Gagal memuat data konflik IP');
      }
    } catch (e) {
      toast.error('Error: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConflicts(); }, [selectedBranch]);

  // Unique routers that currently have at least one conflict group
  const routerOptions = React.useMemo(() => {
    const map = new Map();
    (data.conflicts || []).forEach(c => {
      const rid = c.router_id == null ? 'null' : String(c.router_id);
      if (!map.has(rid)) {
        map.set(rid, { id: rid, name: c.router_name || (rid === 'null' ? '(Tanpa Router)' : `Router #${rid}`), count: 0 });
      }
      map.get(rid).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [data.conflicts]);

  // Apply client-side router filter
  const filteredConflicts = React.useMemo(() => {
    if (routerFilter === 'all') return data.conflicts || [];
    return (data.conflicts || []).filter(c => {
      const rid = c.router_id == null ? 'null' : String(c.router_id);
      return rid === routerFilter;
    });
  }, [data.conflicts, routerFilter]);

  const filteredTotalCustomers = filteredConflicts.reduce((sum, c) => sum + (c.count || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 sm:p-6 space-y-4" data-testid="ip-conflict-report-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2 dark:text-slate-200">
            <AlertTriangle className="text-amber-500" size={28} />
            Customer IP Conflict
          </h1>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Daftar pelanggan dengan alamat IP duplikat di tenant ini. 2+ pelanggan dengan IP sama tidak dapat online bersamaan.
          </p>
        </div>
        <Button onClick={fetchConflicts} disabled={loading} variant="outline" data-testid="refresh-conflicts-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 border-l-4 border-l-amber-500 bg-amber-50/50">
          <div className="text-xs text-amber-700 uppercase font-semibold dark:text-amber-300">IP Bermasalah</div>
          <div className="text-3xl font-bold text-amber-900 mt-1" data-testid="total-conflict-ips">
            {data.total_conflict_ips}
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-red-500 bg-red-50/50">
          <div className="text-xs text-red-700 uppercase font-semibold dark:text-red-300">Total Pelanggan Terdampak</div>
          <div className="text-3xl font-bold text-red-900 mt-1" data-testid="total-conflict-customers">
            {data.total_conflict_customers}
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-blue-500 bg-blue-50/50">
          <div className="text-xs text-blue-700 uppercase font-semibold dark:text-blue-300">Status</div>
          <div className="text-lg font-bold text-blue-900 mt-1">
            {loading ? 'Memuat...' : (data.total_conflict_ips === 0 ? '✓ Tidak Ada Konflik' : '⚠ Perlu Tindakan')}
          </div>
        </Card>
      </div>

      {/* Router Filter Pills */}
      {routerOptions.length > 0 && (
        <Card className="p-3 bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-700" data-testid="router-filter-card">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5 dark:text-slate-300">
              <Router className="w-3.5 h-3.5" /> Filter Router:
            </span>
            <button
              type="button"
              onClick={() => setRouterFilter('all')}
              data-testid="router-filter-all"
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${routerFilter === 'all' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400 hover:text-indigo-600'}`}
            >
              Semua Router <span className="opacity-70">({data.conflicts.length})</span>
            </button>
            {routerOptions.map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setRouterFilter(opt.id)}
                data-testid={`router-filter-${opt.id}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${routerFilter === opt.id ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400 hover:text-amber-600'}`}
              >
                {opt.name} <span className="opacity-70">({opt.count})</span>
              </button>
            ))}
            {routerFilter !== 'all' && (
              <button
                type="button"
                onClick={() => setRouterFilter('all')}
                className="ml-auto px-2 py-1 rounded-full text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1 dark:text-slate-400"
                data-testid="router-filter-clear"
              >
                <X className="w-3 h-3" /> Reset
              </button>
            )}
          </div>
          {routerFilter !== 'all' && (
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Menampilkan <b className="text-slate-700 dark:text-slate-200">{filteredConflicts.length}</b> grup konflik
              ({filteredTotalCustomers} pelanggan) di router terpilih.
            </div>
          )}
        </Card>
      )}

      {/* Conflict groups */}
      {!loading && data.conflicts.length === 0 && (
        <Card className="p-8 text-center">
          <div className="text-green-600 text-5xl mb-2 dark:text-green-400">✓</div>
          <p className="text-lg font-semibold text-slate-700 dark:text-slate-200">Semua IP unik per tenant.</p>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">Tidak ada konflik IP di antara pelanggan Anda.</p>
        </Card>
      )}

      {!loading && data.conflicts.length > 0 && filteredConflicts.length === 0 && (
        <Card className="p-6 text-center bg-slate-50 dark:bg-slate-900" data-testid="filter-empty-state">
          <p className="text-sm text-slate-600 dark:text-slate-300">Tidak ada konflik untuk router yang dipilih.</p>
          <button
            type="button"
            className="mt-2 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
            onClick={() => setRouterFilter('all')}
          >Reset filter</button>
        </Card>
      )}

      {filteredConflicts.map((grp, idx) => (
        <Card key={grp.ip} className="overflow-hidden border-l-4 border-l-amber-500" data-testid={`conflict-group-${idx}`}>
          <div className="bg-amber-50 px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2 dark:bg-amber-900/30">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-amber-600 dark:text-amber-400" size={20} />
              <div>
                <span className="text-sm text-amber-700 dark:text-amber-300">IP Duplikat:</span>{' '}
                <span className="font-mono font-bold text-amber-900 text-lg">{grp.ip}</span>
                {grp.router_name && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded bg-amber-200 text-amber-800 font-medium">@ {grp.router_name}</span>
                )}
              </div>
            </div>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40">
              {grp.count} Pelanggan
            </Badge>
          </div>
          <div className="divide-y">
            {grp.customers.map((c) => (
              <div key={c.id} className="px-4 py-3 flex items-center justify-between flex-wrap gap-2 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{c.name}</span>
                    <span className="text-xs text-slate-400 font-mono dark:text-slate-500">#{c.account_number}</span>
                    {c.is_online ? (
                      <Badge className="bg-emerald-500 text-white text-[10px] h-5"><Wifi className="w-3 h-3 mr-1" />Online</Badge>
                    ) : (
                      <Badge className="bg-slate-300 text-slate-700 text-[10px] h-5 dark:text-slate-200"><WifiOff className="w-3 h-3 mr-1" />Offline</Badge>
                    )}
                    <Badge variant="outline" className="text-[10px] h-5">{c.connection_type}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 space-x-2 dark:text-slate-400">
                    <span>Owner: <b>{c.owner_name || '-'}</b></span>
                    <span>· Cabang: <b>{c.branch_name || '-'}</b></span>
                    <span>· Paket: <b>{c.service_plan_name || '-'}</b></span>
                    {c.current_mac && <span>· MAC: <code>{c.current_mac}</code></span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/app/customers/${c.id}`)}
                    data-testid={`view-customer-${c.id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" />
                    Detail
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/30"
                    onClick={() => navigate(`/app/customers?edit=${c.id}`)}
                    data-testid={`edit-customer-${c.id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-1" />
                    Edit IP
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}

      {data.conflicts.length > 0 && (
        <Card className="p-4 bg-slate-50 border-dashed dark:bg-slate-900">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            <b>💡 Saran:</b> Untuk tiap kelompok IP duplikat, hanya 1 pelanggan yang akan berhasil online di waktu yang sama
            (yang MAC-nya match dengan ARP entry di router). Periksa modem di lapangan, lalu update IP yang benar atau hapus pelanggan duplikat.
          </p>
        </Card>
      )}
    </div>
  );
};

export default IPConflictReport;
